import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AdminButton from 'components/common/AdminButton';
import { format } from 'date-fns';

/**
 * Modal for deactivating an assignment
 */
function DeactivateAssignmentModal({ 
  open, 
  onClose, 
  assignment, 
  onDeactivate, 
  currentUser 
}) {
  const [endDate, setEndDate] = useState(new Date());
  const [changeReason, setChangeReason] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    if (!isSubmitting) {
      setEndDate(new Date());
      setChangeReason('');
      setErrors({});
      onClose();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!endDate) {
      newErrors.endDate = 'End date is required';
    } else {
      const startDate = new Date(assignment?.startDate);
      if (endDate < startDate) {
        newErrors.endDate = 'End date cannot be before start date';
      }
    }
    
    if (!changeReason.trim()) {
      newErrors.changeReason = 'Change reason is required';
    } else if (changeReason.trim().length < 10) {
      newErrors.changeReason = 'Please provide a more detailed reason (min 10 characters)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Convert the date to ISO 8601 format for AWSDateTime
      const isoEndDate = endDate ? new Date(endDate).toISOString() : null;
      
      await onDeactivate({
        assignmentId: assignment.id,
        assignmentType: assignment.type,
        endDate: isoEndDate,
        changeReason: changeReason.trim(),
        changedBy: currentUser?.email || currentUser?.username || 'Unknown User',
        isActive: false
      });
      
      handleClose();
    } catch (error) {
      setErrors({ submit: 'Failed to deactivate assignment. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!assignment) return null;

  const assignmentName = assignment.type === 'location' 
    ? assignment.locationName 
    : assignment.orgUnitName;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Deactivate Assignment
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            You are about to deactivate the following assignment:
            <Box sx={{ mt: 1 }}>
              <strong>{assignmentName}</strong> - {assignment.roleName}
            </Box>
          </Alert>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date *"
              value={endDate}
              onChange={(newValue) => {
                setEndDate(newValue);
                if (errors.endDate) {
                  setErrors({ ...errors, endDate: null });
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.endDate,
                  helperText: errors.endDate,
                  sx: { mb: 3 }
                }
              }}
            />
          </LocalizationProvider>

          <TextField
            label="Change Reason *"
            multiline
            rows={3}
            fullWidth
            value={changeReason}
            onChange={(e) => {
              setChangeReason(e.target.value);
              if (errors.changeReason) {
                setErrors({ ...errors, changeReason: null });
              }
            }}
            error={!!errors.changeReason}
            helperText={errors.changeReason || 'Please provide a detailed reason for deactivating this assignment'}
            placeholder="e.g., Employee transferred to different location, Role change, Termination, etc."
          />

          {errors.submit && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.submit}
            </Alert>
          )}

          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Assignment Start Date:</strong> {format(new Date(assignment.startDate), 'MM/dd/yyyy')}
            </Typography>
            <br />
            <Typography variant="caption" color="text.secondary">
              <strong>Changed By:</strong> {currentUser?.email || currentUser?.username || 'Unknown User'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <AdminButton 
          variant="outlined" 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </AdminButton>
        <AdminButton 
          variant="contained" 
          color="error"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Deactivating...' : 'Deactivate Assignment'}
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
}

DeactivateAssignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  assignment: PropTypes.object,
  onDeactivate: PropTypes.func.isRequired,
  currentUser: PropTypes.object
};

export default DeactivateAssignmentModal;